import React from 'react'
import {
  Warehouse,
  ShieldCheck,
  Package,
  Layers3,
  Truck,
  CheckCircle,
  MoveRight,
  ClipboardCheck,
  ArrowRight,
  PackageCheck,
  Barcode,
  Headphones
} from 'lucide-react'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'

import processImage from '../assets/images/data/warehouse.png'
import MoreServicesSection from './MoreServicesSection'
import CTA from './CTA'

const services = [
  {
    icon: <Warehouse className='w-6 h-6' />,
    title: 'Storage Solutions',
    description:
      'Flexible warehouse storage options designed to support different business and inventory requirements.'
  },
  {
    icon: <Package className='w-6 h-6' />,
    title: 'Inventory Management',
    description:
      'Organized inventory handling and stock visibility to support efficient warehouse operations.'
  },
  {
    icon: <Layers3 className='w-6 h-6' />,
    title: 'Palletized Storage',
    description:
      'Structured pallet storage and space utilization according to cargo and operational needs.'
  },
  {
    icon: <PackageCheck className='w-6 h-6' />,
    title: 'Order Fulfillment',
    description:
      'Coordinated picking, packing, and dispatch operations to support order fulfillment requirements.'
  },
  {
    icon: <Truck className='w-6 h-6' />,
    title: 'Inbound & Outbound Handling',
    description:
      'Organized receiving, dispatch, and movement of goods throughout warehouse operations.'
  },
  {
    icon: <Barcode className='w-6 h-6' />,
    title: 'Barcode-Based Operations',
    description:
      'Barcode-enabled inventory identification and tracking to support warehouse accuracy.'
  }
]

const benefits = [
  {
    icon: <ShieldCheck className='w-6 h-6' />,
    title: 'Secure Storage',
    description:
      'Warehouse security practices and access management designed to protect stored inventory.'
  },
  {
    icon: <Layers3 className='w-6 h-6' />,
    title: 'Flexible Capacity',
    description:
      'Storage arrangements designed to accommodate changing inventory and business requirements.'
  },
  {
    icon: <PackageCheck className='w-6 h-6' />,
    title: 'Inventory Visibility',
    description:
      'Organized stock management and inventory reporting for improved operational visibility.'
  },
  {
    icon: <Headphones className='w-6 h-6' />,
    title: 'Operational Support',
    description:
      'Coordinated warehouse assistance for receiving, storage, packing, and dispatch requirements.'
  }
]

const processSteps = [
  {
    number: '01',
    icon: <ClipboardCheck className='w-6 h-6' />,
    title: 'Requirement Planning',
    description:
      'Understand your storage volume, inventory type, handling needs, and distribution requirements.'
  },
  {
    number: '02',
    icon: <Truck className='w-6 h-6' />,
    title: 'Inbound Receiving',
    description:
      'Receive incoming goods and coordinate their initial warehouse processing.'
  },
  {
    number: '03',
    icon: <Warehouse className='w-6 h-6' />,
    title: 'Storage & Inventory',
    description:
      'Organize goods into suitable storage locations with inventory management support.'
  },
  {
    number: '04',
    icon: <PackageCheck className='w-6 h-6' />,
    title: 'Order Processing & Dispatch',
    description:
      'Coordinate picking, packing, and outbound dispatch according to order requirements.'
  }
]

const industries = [
  {
    title: 'Manufacturing & Industrial'
  },
  {
    title: 'Retail & E-Commerce'
  },
  {
    title: 'Wholesale & Distribution'
  },
  {
    title: 'Consumer Goods'
  }
]

const capabilities = [
  {
    title: 'Inbound Receiving & Handling',
    desc: 'Smooth receiving and handling of incoming goods.'
  },
  {
    title: 'Organized Warehouse Storage',
    desc: 'Safe and organized storage for all cargo.'
  },
  {
    title: 'Inventory & Barcode Management',
    desc: 'Accurate stock tracking with barcode technology.'
  },
  {
    title: 'Order Fulfillment & Dispatch',
    desc: 'Fast order processing, packing, and dispatch.'
  }
]

const WarehousingPage = () => {
  return (
    <main className='bg-white text-gray-800 overflow-hidden relative font-sans'>
      <Helmet>
        <title>Warehousing Services in India | S INFINITE EXPRESS</title>

        <meta
          name='description'
          content='Explore warehousing and storage solutions by S INFINITE EXPRESS. Discover inventory management, secure storage, inbound and outbound handling, palletized storage, and fulfillment support.'
        />

        <meta
          name='keywords'
          content='Warehousing Services India, Warehouse Storage, Inventory Management, 3PL Warehousing, Fulfillment Solutions, Palletized Storage, Warehouse Distribution, S INFINITE EXPRESS'
        />

        <meta name='robots' content='index, follow' />

        <link
          rel='canonical'
          href='https://sinfiniteexpress.com/services/warehousing'
        />

        <meta
          property='og:title'
          content='Warehousing Services | S INFINITE EXPRESS'
        />

        <meta
          property='og:description'
          content='Flexible warehousing, inventory management, storage, and fulfillment solutions by S INFINITE EXPRESS.'
        />

        <meta
          property='og:url'
          content='https://sinfiniteexpress.com/services/warehousing'
        />

        <meta property='og:type' content='website' />
      </Helmet>

      {/* HERO SECTION */}
      <section className='relative bg-gradient-to-br from-white via-blue-50/40 to-white overflow-hidden'>
        <div className='absolute top-0 right-0 w-96 h-96 bg-blue-100/40 rounded-full blur-3xl' />
        <div className='absolute bottom-0 left-0 w-72 h-72 bg-[#fff44f]/20 rounded-full blur-3xl' />

        <div className='relative max-w-7xl mx-auto px-6 md:px-12 py-8 md:py-12'>
          <div className='grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center'>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className='max-w-2xl'
            >
              <h1 className='text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-extrabold text-[#211f57] leading-tight mb-3'>
                Smart Warehousing
                <span className='block text-[#d32c30] mt-1'>Solutions.</span>
              </h1>

              <p className='text-gray-600 text-sm md:text-base leading-relaxed mb-4'>
                Simplify your storage and distribution operations with S
                INFINITE EXPRESS. From inventory management and organized
                storage to order fulfillment and dispatch, we help keep your
                supply chain moving efficiently.
              </p>

              <p className='text-gray-600 text-sm md:text-base leading-relaxed mb-6'>
                Our warehousing solutions are designed to support your business
                with coordinated handling, flexible storage, and efficient
                inventory operations.
              </p>

              <div className='flex flex-wrap gap-3'>
                <a
                  href='/get-a-quote'
                  className='inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#d32c30] text-white rounded-lg font-bold text-sm hover:bg-[#b82629] transition-all duration-300 shadow-md'
                >
                  Get a Warehousing Quote
                  <MoveRight className='w-4 h-4' />
                </a>

                <a
                  href='/contact-us'
                  className='inline-flex items-center justify-center gap-2 px-6 py-3 border border-[#211f57] text-[#211f57] rounded-lg font-bold text-sm hover:bg-[#211f57] hover:text-white transition-all duration-300'
                >
                  Contact Us
                  <ArrowRight className='w-4 h-4' />
                </a>
              </div>

              <div className='flex flex-wrap gap-x-6 gap-y-3 mt-7 text-[#211f57] text-xs sm:text-sm font-semibold'>
                <div className='flex items-center gap-2'>
                  <CheckCircle className='w-4 h-4 text-[#d32c30]' />
                  Organized Storage
                </div>

                <div className='flex items-center gap-2'>
                  <CheckCircle className='w-4 h-4 text-[#d32c30]' />
                  Inventory Management
                </div>

                <div className='flex items-center gap-2'>
                  <CheckCircle className='w-4 h-4 text-[#d32c30]' />
                  Fulfillment Support
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className='relative w-full'
            >
              <div className='relative rounded-2xl overflow-hidden shadow-xl border border-gray-100'>
                <img
                  src={processImage}
                  alt='Modern warehouse storage and logistics operations'
                  className='w-full h-[200px] sm:h-[300px] lg:h-[420px] object-cover object-center'
                  fetchPriority='high'
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* INTRODUCTION / CAPABILITIES */}
      <section className='py-8 md:py-10 bg-gradient-to-r from-[#fffef0] via-white to-blue-50/60'>
        <div className='max-w-7xl mx-auto px-6 md:px-12'>
          <div className='grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-8 lg:gap-12 items-center'>
            <motion.div
              initial={{ opacity: 0, y: 25 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className='text-[#d32c30] font-extrabold uppercase tracking-[0.18em] text-xs'>
                Integrated Warehousing Solutions
              </span>

              <h2 className='text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#211f57] leading-tight mt-3 mb-4'>
                Storage That Supports
                <span className='text-[#d32c30]'> Your Growth.</span>
              </h2>

              <div className='w-16 h-1 bg-[#d32c30] rounded-full mb-5' />

              <p className='text-gray-600 text-sm md:text-base leading-relaxed mb-4'>
                <span className='font-bold text-[#d32c30]'>
                  S INFINITE EXPRESS
                </span>{' '}
                provides warehousing solutions designed to support modern
                storage, inventory, and distribution requirements. Our approach
                focuses on organized storage, efficient handling, and
                coordinated warehouse operations.
              </p>

              <p className='text-gray-600 text-sm leading-relaxed mb-5'>
                From receiving and inventory organization to order processing
                and dispatch, we help businesses manage their goods through a
                coordinated warehousing process.
              </p>

              <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5'>
                {[
                  'Organized Inventory Handling',
                  'Flexible Storage Options',
                  'Inbound & Outbound Support',
                  'Order Fulfillment'
                ].map((item, index) => (
                  <div key={index} className='flex items-center gap-2'>
                    <CheckCircle className='w-4 h-4 shrink-0 text-[#d32c30]' />
                    <span className='text-[#211f57] text-xs md:text-sm font-semibold'>
                      {item}
                    </span>
                  </div>
                ))}
              </div>

              <a
                href='/get-a-quote'
                className='inline-flex items-center gap-2 text-[#d32c30] font-extrabold text-sm hover:gap-4 transition-all duration-300'
              >
                Discuss Your Storage Requirements
                <ArrowRight className='w-4 h-4' />
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 25 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className='relative'
            >
              {/* BACKGROUND GLOW */}
              <div className='absolute -right-5 -top-6 h-32 w-32 rounded-full bg-[#fff44f]/50 blur-3xl' />
              <div className='absolute -bottom-6 -left-5 h-32 w-32 rounded-full bg-blue-200/70 blur-3xl' />

              {/* MAIN CARD */}
              <div className='relative overflow-hidden rounded-3xl bg-[#211f57] p-3 shadow-2xl sm:p-4 lg:p-5'>
                {/* DECORATIVE ELEMENTS */}
                <div className='absolute -right-16 -top-16 h-48 w-48 rounded-full border-[25px] border-white/5' />
                <div className='absolute -bottom-20 -right-10 h-56 w-56 rounded-full border-[25px] border-white/5' />

                <div className='relative z-10'>
                  {/* CARD HEADER */}
                  <div className='mb-2 flex items-center gap-4'>
                    <span className='text-xs font-bold uppercase tracking-[2px] text-[#fff44f]'>
                      Our Capabilities
                    </span>
                  </div>

                  <p className='text-white/70 text-sm leading-relaxed mb-5'>
                    Coordinated warehousing operations to support your storage,
                    inventory, and order distribution requirements.
                  </p>

                  <div className='space-y-3'>
                    {capabilities.map((item, index) => (
                      <div
                        key={index}
                        className='flex items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 transition-all duration-300 hover:bg-white/10'
                      >
                        <div>
                          <h4 className='text-sm font-bold text-white sm:text-base'>
                            {item.title}
                          </h4>
                          <p className='mt-1 text-xs leading-5 text-white/55 sm:text-sm'>
                            {item.desc}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WAREHOUSING SERVICES */}
      <section className='py-5 md:py-8 bg-[#f7f8fc]'>
        <div className='max-w-7xl mx-auto px-6 md:px-12'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className='text-center max-w-4xl mx-auto mb-5 md:mb-5'
          >
            <span className='text-[#d32c30] font-extrabold uppercase tracking-[0.18em] text-xs'>
              What We Offer
            </span>

            <h2 className='text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#211f57] mb-2'>
              Our Warehousing Services
            </h2>

            <p className='text-gray-600 text-sm md:text-base leading-relaxed'>
              Discover flexible storage, inventory handling, and fulfillment
              solutions designed around your business requirements.
            </p>
          </motion.div>

          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6'>
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                className='group bg-white rounded-xl p-5 md:p-6 border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300'
              >
                <div className='w-12 h-12 rounded-xl bg-[#211f57] text-[#ffff] flex items-center justify-center mb-4 group-hover:bg-[#d32c30] group-hover:text-white transition-colors duration-300'>
                  {service.icon}
                </div>

                <h3 className='text-base md:text-lg font-extrabold text-[#211f57] mb-2'>
                  {service.title}
                </h3>

                <p className='text-gray-600 text-xs md:text-sm leading-relaxed'>
                  {service.description}
                </p>

                <div className='mt-4 flex items-center gap-2 text-[#d32c30] font-bold text-xs'>
                  <span>Explore Solutions</span>
                  <ArrowRight className='w-4 h-4 group-hover:translate-x-1 transition-transform' />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className='py-5 md:py-8 bg-gradient-to-br from-[#fffef0] via-white to-blue-50/70'>
        <div className='max-w-7xl mx-auto px-6 md:px-12'>
          <div className='text-center max-w-4xl mx-auto mb-4 md:mb-8'>
            <span className='text-[#d32c30] font-extrabold uppercase tracking-[0.18em] text-xs'>
              The S Infinite Advantage
            </span>

            <h2 className='text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#211f57] mb-2'>
              Why Choose Our Warehousing Solutions?
            </h2>

            <p className='text-gray-600 text-sm md:text-base leading-relaxed'>
              A coordinated warehousing approach focused on secure storage,
              organized inventory, flexible capacity, and efficient handling.
            </p>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5'>
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.06 }}
                className='group bg-white p-5 md:p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all duration-300'
              >
                <div className='w-12 h-12 rounded-xl bg-[#211f57] text-[#ffff] flex items-center justify-center mb-4 group-hover:bg-[#d32c30] group-hover:text-white transition-colors'>
                  {benefit.icon}
                </div>

                <h3 className='text-base font-extrabold text-[#211f57] mb-2'>
                  {benefit.title}
                </h3>

                <p className='text-xs md:text-sm leading-relaxed text-gray-600'>
                  {benefit.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WAREHOUSING PROCESS */}
      <section className='py-5 md:py-8 bg-white'>
        <div className='max-w-7xl mx-auto px-6 md:px-12'>
          <div className='text-center max-w-4xl mx-auto mb-5 md:mb-5'>
            <span className='text-[#d32c30] font-extrabold uppercase tracking-[0.18em] text-xs'>
              How It Works
            </span>

            <h2 className='text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#211f57] mb-2'>
              Our Warehousing Process
            </h2>

            <p className='text-gray-600 text-sm md:text-base leading-relaxed'>
              From inbound receiving to outbound dispatch, every stage is
              coordinated to support organized warehouse operations.
            </p>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8'>
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                className='relative text-center group'
              >
                <div className='relative w-16 h-16 mx-auto mb-4 rounded-xl bg-[#211f57] text-[#ffff] flex items-center justify-center shadow-md group-hover:bg-[#d32c30] group-hover:text-white transition-all duration-300'>
                  {step.icon}
                </div>

                {index !== 3 && (
                  <div className='hidden lg:block absolute top-8 left-[65%] w-[75%] border-t-2 border-dashed border-gray-300' />
                )}

                <h3 className='text-base font-extrabold text-[#211f57] mb-2'>
                  {step.title}
                </h3>

                <p className='text-xs md:text-sm text-gray-600 leading-relaxed max-w-xs mx-auto'>
                  {step.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES WE SERVE */}
      <section className='py-8 md:py-10 bg-[#f7f8fc]'>
        <div className='max-w-7xl mx-auto px-6 md:px-12'>
          <div className='grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-8 lg:gap-12 items-center'>
            <div>
              <span className='text-[#d32c30] font-extrabold uppercase tracking-[0.18em] text-xs'>
                Industry Expertise
              </span>

              <h2 className='text-2xl md:text-3xl lg:text-4xl font-extrabold text-[#211f57] mt-3 mb-4'>
                Warehousing for Every Business Need
              </h2>

              <div className='w-16 h-1 bg-[#d32c30] rounded-full mb-5' />

              <p className='text-gray-600 text-sm md:text-base leading-relaxed mb-5'>
                Different industries require different storage and inventory
                management approaches. Our warehousing solutions are designed to
                support a range of business and distribution requirements.
              </p>

              <a
                href='/industry-expertise'
                className='inline-flex items-center gap-2 px-5 py-3 bg-[#211f57] text-white rounded-lg font-bold text-sm hover:bg-[#d32c30] transition-all duration-300'
              >
                Explore Industries
                <MoveRight className='w-4 h-4' />
              </a>
            </div>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
              {industries.map((industry, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.06 }}
                  className='flex items-center gap-4 bg-white p-5 rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300'
                >
                  <h3 className='text-sm md:text-base font-extrabold text-[#211f57]'>
                    {industry.title}
                  </h3>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <MoreServicesSection />
      <CTA />
    </main>
  )
}

export default WarehousingPage
